import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import _ from "lodash";
import React from "react";
import { TransformedEvent, UntransformedEvent } from "../transformationDefinition";

const useStyles = makeStyles((theme) => ({
    beforeAndAfter: {
        display: "flex",
        justifyContent: "center",
    },
    transformBeforeOrAfter: {
        width: "50%",
        border: "1px solid #CCC",
        fontFamily: "\"Monospace\", monospace",
    },
    transformBeforeOrAfterTitle: {
        fontWeight: "bold",
        backgroundColor: "#EFEFEF",
        borderBottom: "1px solid #CCC",
        color: "#444",
        width: "100%",
        paddingTop: theme.spacing(.25),
        paddingBottom: theme.spacing(.25),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        whiteSpace: "pre",
        textTransform: "uppercase",
        fontSize: 12,
    },
    transformBeforeOrAfterContent: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },
    transformBeforeOrAfterLine: {
        whiteSpace: "pre-wrap",
        paddingTop: theme.spacing(.25),
        paddingBottom: theme.spacing(.25),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    beforeAndAfterDiff: {
        minWidth: 650,
        maxWith: 850,
        width: "60%",
        display: "flex",
    },
    transformBeforeOrAfterLineChanged: {
        backgroundColor: "#EFF1F9",
    },
    transformAfterSectionChanged: {
        backgroundColor: "#B3BBE5",
    },
}));

const BeforeComparisonLine: React.FC<{
    indent: number;
    propKey: string;
    propValue: string | boolean | number;
    lineChanged: boolean;
    editable?: boolean;
}> = ({ indent, propKey, propValue, lineChanged, editable = false }) => {
    const classes = useStyles();
    const text = <>{" ".repeat(indent * 2)}"<span>{propKey}</span>": <span>{JSON.stringify(propValue)}</span>,</>;

    return (
        <div
            className={clsx(classes.transformBeforeOrAfterLine, lineChanged && classes.transformBeforeOrAfterLineChanged)}>
            {text}
        </div>
    );

};
const AfterComparisonLine: React.FC<{
    indent: number;
    propKey: string;
    propValue: string | boolean | number;
    keyChanged: boolean;
    valueChanged: boolean;
}> = ({ indent, propKey, propValue, keyChanged, valueChanged }) => {
    const classes = useStyles();
    const renderedKey = (
        <span className={clsx(keyChanged && classes.transformAfterSectionChanged)}>{propKey}</span>
    );
    const renderedProp = (
        <span className={clsx(valueChanged && classes.transformAfterSectionChanged)}>{JSON.stringify(propValue)}</span>
    );

    const lineChanged = keyChanged || valueChanged;

    return (
        <div
            className={clsx(classes.transformBeforeOrAfterLine, lineChanged && classes.transformBeforeOrAfterLineChanged)}>
            {" ".repeat(indent * 2)}"{renderedKey}": {renderedProp},
        </div>
    );
};

export const BeforeAndAfterComparison: React.FC<{
    before: UntransformedEvent;
    after: TransformedEvent;
}> = ({ before, after }) => {
    const classes = useStyles();

    const beforeRendered = _.map(before.eventProperties, (propValue: any, propKey: string) => (
        <BeforeComparisonLine
            key={propKey}
            indent={3}
            editable={true}
            propKey={propKey}
            propValue={propValue}
            lineChanged={before.eventProperties[propKey] !== after.eventProperties[propKey]}/>
    ));

    const afterRendered = _.map(after.eventProperties, (propValue: any, propKey: string) => {
        const keyBefore = _.findKey(after.renamedProperties, (v) => v == propKey);
        const keyAfter = after.renamedProperties[propKey] || propKey;
        // @ts-ignore
        const valueBefore = before.eventProperties[keyBefore];
        const valueAfter = after.eventProperties[keyAfter];
        const keyChanged = keyBefore !== keyAfter;
        const valueChanged = valueAfter !== valueBefore;

        return (
            <AfterComparisonLine
                key={propKey}
                indent={3}
                propKey={propKey}
                propValue={propValue}
                keyChanged={keyChanged}
                valueChanged={valueChanged}/>
        );
    });

    return (
        <div className={classes.beforeAndAfter}>
            <div className={classes.beforeAndAfterDiff}>
                <div className={classes.transformBeforeOrAfter}>
                    <div className={classes.transformBeforeOrAfterTitle}>Before</div>
                    <div className={classes.transformBeforeOrAfterContent}>
                        <div className={classes.transformBeforeOrAfterLine}>{"{"}</div>
                        <BeforeComparisonLine indent={1} propKey={"event"} propValue={before.eventName}
                                              lineChanged={before.eventName !== after.eventName}/>
                        <div className={classes.transformBeforeOrAfterLine}>{"    \"properties\": {"}</div>
                        <div className={classes.transformBeforeOrAfterLine}>{"      ..."}</div>
                        {beforeRendered}
                        {Object.keys(before.eventProperties).length > 1 &&
                        <div className={classes.transformBeforeOrAfterLine}>{"      ..."}</div>}
                        <div className={classes.transformBeforeOrAfterLine}>{"    }"}</div>
                        <div className={classes.transformBeforeOrAfterLine}>{"}"}</div>
                    </div>
                </div>
                <div className={classes.transformBeforeOrAfter}>
                    <div className={classes.transformBeforeOrAfterTitle}>After</div>
                    <div className={classes.transformBeforeOrAfterContent}>
                        <div className={classes.transformBeforeOrAfterLine}>{"{"}</div>
                        <AfterComparisonLine indent={1} propKey={"event"} propValue={after.eventName} keyChanged={false}
                                             valueChanged={before.eventName !== after.eventName}/>
                        <div className={classes.transformBeforeOrAfterLine}>{"    \"properties\": {"}</div>
                        <div className={classes.transformBeforeOrAfterLine}>{"      ..."}</div>
                        {afterRendered}
                        {Object.keys(after.eventProperties).length > 1 &&
                        <div className={classes.transformBeforeOrAfterLine}>{"      ..."}</div>}
                        <div className={classes.transformBeforeOrAfterLine}>{"    }"}</div>
                        <div className={classes.transformBeforeOrAfterLine}>{"}"}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};